import { useEffect, useRef, useState, useMemo } from 'react';
import { ModalWithForm } from '../modal';
import { useShowToast } from '@/hooks';
import { AlreadyPartOf } from './components';
import { resolveSelectedContactIds } from './resolveSelectedContactIds';
import { useImportContactsMutation } from '@/store/query/contacts-list';
import { useGetAllJobsQuery } from '@/store/query/ats-jobs';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
  CandidateSourceMenu,
  CandidateSourceSingleValue,
} from '@/components/formBuilder/SelectComponents';
import { useGetCandidateSourcesQuery } from '@/store/query/candidate-sources';
import { JobListMenu } from '../filters/components/UiItems/JobList';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';
import { BulkError } from '../BulkError';
import { useDisclosure } from '@chakra-ui/react';
import { FormNotes } from '@/components/formBuilder/components/FormNotes';
import { useGetContactByIdQuery } from '@/store/query/contacts';

const errorMap = {
  application_exists: 'Contact already present in the job',
};

export const AddToJob = ({
  modalActions,
  candidateId,
  isBulk = false,
  onSuccess = () => {},
  alreadyPartOfProps = {},
  existingJobs = [],
  enableStageOption = false,
  triggerRef = 'project',
  bulkOpMethods = null,
  selected = [], // Selected contacts with full data
  enableBulkActivity = false, // Only true from Jobs and Contacts pages
  enableApi = false,
}) => {
  const [errorContacts, setErrorContacts] = useState(null);
  // Bulk operation tracking
  const internalBulkOp = useBulkOperation({
    activityType: 'add_to_job',
  });
  const selectedContact = Array.isArray(selected)
    ? selected.length === 1
      ? selected[0]
      : {}
    : selected || {};
  const { ats_candidate_source = {}, credited_to = {} } = selectedContact;

  //for flows & projects
  const { data: contactData } = useGetContactByIdQuery(
    {
      contactId: candidateId.length > 1 ? candidateId[0] : candidateId,
    },
    { skip: !enableApi }
  );

  const errorsActions = useDisclosure();
  // Use external bulkOpMethods if provided, otherwise use internal
  const { startTracking, completeSuccess, completeWithErrors, completeFailed } =
    bulkOpMethods || internalBulkOp;

  // Get contact IDs for the action. `selected` may be raw contacts, applications
  // with a nested contact, or react-table rows ({ original }) from Flows/Projects
  // bulk actions — resolveSelectedContactIds handles all shapes. See its doc.
  const targetIds = useMemo(() => {
    if (isBulk && selected?.length > 0) {
      return resolveSelectedContactIds(selected);
    }
    return Array.isArray(candidateId)
      ? candidateId
      : [candidateId].filter(Boolean);
  }, [isBulk, selected, candidateId]);
  const [stageOptions, setStageOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const formRef = useRef(null);
  const { data: ats_jobs = [], isLoading: isJobsOptionsLoading } =
    useGetAllJobsQuery(
      {
        params: {
          view: 'interview_plan',
          scope: 'viewable',
          filter_by: { job_statuses: ['published', 'scheduled'] },
          sort_by: { open_at: 'DESC' },
          limit: 500,
        },
      },
      { skip: isLoading }
    );
  const { data: candidateSources } = useGetCandidateSourcesQuery(
    {},
    { skip: isLoading }
  );
  const availableSources = candidateSources?.filter(
    (item) => item?.enabled && !item?.deleted
  );
  const toast = useShowToast();
  const currUser = useSelector(selectCurrUser);
  const users = useSelector(selectUsers);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const usersOptions = users?.map((user) => ({
    ...user,
    label: user?.name,
    value: user?.id,
  }));

  const existingJobIds = existingJobs?.map((job) => job?.job_id) || [];

  const switchLabelStyles = {
    size: 'ats-sm-regular',
    marginBottom: '0',
    cursor: 'pointer',
  };

  const labelStyles = {
    size: 'ats-sm-bold',
  };

  const formConfig = [
    ...(triggerRef === 'job'
      ? [
          {
            name: 'note_banner',
            type: 'custom',
            Field: () => (
              <FormNotes
                noteValue="The candidate will remain in this job and also be added to the new one"
                props={{
                  mt: '12px',
                  p: '8px 2px',
                }}
              />
            ),
          },
        ]
      : []),
    {
      name: 'job_id',
      label: 'Select job ',
      type: 'kSelect',
      placeholder: 'Select job',
      labelStyles: labelStyles,
      newStyles: [
        {
          type: 'menu',
          newStyles: {
            padding: '0px !important',
            borderRadius: '6px !important',
            border: '1px solid #EDEDED',
            borderColor: 'border-color',
            boxShadow: 'none',
          },
        },
      ],
      isRequired: true,
      isLoading: isJobsOptionsLoading,
      options: ats_jobs
        ?.filter((item) => !existingJobIds.includes(item?.id))
        ?.map((item) => ({
          value: item?.id,
          label: item?.name,
          status: item?.state,
          department: item?.department,
          locations: item?.offices || [],
          ...item,
        })),
      filterOption: () => true,
      components: {
        Menu: JobListMenu,
        IndicatorSeparator: () => null,
      },
      customOnChange: (value) => {
        const stages = value.stages;
        setStageOptions(stages);
        formRef.current?.setFieldValue('stage_id', null, false);
        formRef.current?.setFieldTouched('stage_id', false, false);
      },
    },
    {
      name: 'stage_id',
      label: 'Select stage',
      placeholder: 'Select stage',
      type: 'kSelect',
      isRequired: true,
      disabled: stageOptions.length === 0,
      labelStyles: labelStyles,
      options: stageOptions,
      isLoading: isJobsOptionsLoading,
      valueKey: 'id',
      labelKey: 'name',
    },
    {
      name: 'source_credits',
      label: 'Change source and credited to',
      type: 'switch',
      labelStyles: switchLabelStyles,
      initialValue: currUser.id,
      style: {
        flexDirection: 'row-reverse',
        justifyContent: 'flex-end',
        gap: '8px',
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'ats_candidate_source_id',
      label: 'Select source',
      placeholder: 'Select source',
      type: 'kSelect',
      labelStyles: labelStyles,
      options: availableSources,
      valueKey: 'id',
      isMulti: false,
      labelKey: 'name',
      menuPortalTarget: document.body,
      menuPosition: 'fixed',
      components: {
        Menu: CandidateSourceMenu,
        SingleValue: CandidateSourceSingleValue,
        IndicatorSeparator: () => null,
      },
      styles: {
        menu: (base) => ({
          ...base,
          padding: 0,
        }),
        menuList: (base) => ({
          ...base,
          padding: 0,
        }),
      },
    },
    {
      isConditional: true,
      conditionConfig: { field: 'source_credits', value: true },
      name: 'credited_to_user_id',
      label: 'Select credited to',
      placeholder: 'Select credited to',
      type: 'kSelect',
      labelStyles: labelStyles,
      isMulti: false,
      width: '100%',
      customFormatLabel: SelectOptionWithAvatar,
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
      options: usersOptions,
      valueKey: 'id',
    },
  ];

  const [importContact, { isLoading: isImportLoading }] =
    useImportContactsMutation();

  const handleSubmit = async (values) => {
    const { job_id, stage_id } = values;
    const selectedJob = ats_jobs?.find((job) => job?.id === job_id);
    const selectedStageId =
      selectedJob?.stages?.find(
        (stage) => stage?.name === 'Application Review' && stage?.position === 2
      )?.id || selectedJob?.stages?.[0]?.id;

    // Use all selected contacts (validation removed - server will handle duplicates)
    const contactsToAdd = isBulk
      ? targetIds
      : Array.isArray(candidateId)
        ? candidateId
        : [candidateId];
    const totalContacts = contactsToAdd.length;

    // Start tracking only when bulk activity is enabled (Jobs & Contacts pages)
    if (enableBulkActivity && startTracking) {
      startTracking(totalContacts);
      modalActions?.onClose(); // Close modal immediately
    }

    const payload = {
      ats_job_id: selectedJob?.id,
      ats_job_stage_id: enableStageOption ? stage_id : selectedStageId,
      contact_ids: contactsToAdd,
      ats_candidate_source_id: values?.ats_candidate_source_id,
      credited_to_user_id: values?.source_credits
        ? values?.credited_to_user_id
        : null,
      ...(enableBulkActivity && { is_bulk_activity: true }),
    };

    try {
      const result: any = await importContact({ payload });
      // Handle both 'error' and 'errors' keys (API may return either)
      const errorList = result?.error || result?.errors || [];

      if (errorList?.length > 0) {
        if (enableBulkActivity) {
          // Jobs & Contacts: use BulkOperationQueue tracking
          if (completeWithErrors) {
            completeWithErrors({
              successCount: totalContacts - errorList.length,
              failedCount: errorList.length,
              errors: errorList.map((e) => ({
                id: e.id || e.contact_id || 'unknown',
                message: e.reason || e.message || 'Failed to add to job',
              })),
            });
          } else if (completeSuccess) {
            completeSuccess(totalContacts);
          }
          onSuccess?.();
        } else if (isBulk) {
          // Projects, MCO, etc.: show BulkError modal
          setErrorContacts(errorList);
          errorsActions?.onOpen();
          onSuccess?.();
        } else {
          // Single candidate: show toast
          const errorMsg =
            errorMap[errorList?.[0]?.reason] ||
            typeof errorList?.[0]?.reason === 'string'
              ? errorList?.[0]?.reason.includes(':')
                ? errorList?.[0]?.reason.split(':')[1]
                : errorList?.[0]?.reason
              : '';
          toast({
            title: `Unable to add candidate`,
            description: errorMsg,
            status: 'error',
          });
        }
      } else {
        if (enableBulkActivity) {
          // Jobs & Contacts: complete via BulkOperationQueue
          if (completeSuccess) {
            completeSuccess(totalContacts);
          }
        } else {
          toast({ title: `Contact added to Job` });
          modalActions?.onClose();
        }
        onSuccess?.();
      }
    } catch {
      if (enableBulkActivity && completeFailed) {
        completeFailed('Failed to add contacts to job');
      } else {
        toast({
          title: 'Unable to add candidate',
          status: 'error',
        });
      }
    }
  };

  return (
    <>
      <ModalWithForm
        isOpen={modalActions?.isOpen}
        onClose={modalActions?.onClose}
        formConfig={formConfig}
        primaryCtaText="Add to job"
        isCtaLoading={isImportLoading}
        headerText={
          triggerRef === 'job' ? 'Add to additional job' : 'Add to job'
        }
        subHeaderText={
          triggerRef === 'job' ? '' : 'Select a job to add all the candidates'
        }
        enableBorder={true}
        handleSubmit={handleSubmit}
        formContainerStyles={{ minHeight: '250px' }}
        showAdditionalBlockAfter={true}
        AdditionalBlockAfter={AlreadyPartOf}
        additionalBlockAfterProps={alreadyPartOfProps}
        customFormRef={formRef}
        initialValues={{
          credited_to_user_id: enableApi
            ? contactData?.credited_to?.id
            : credited_to?.id,
          ats_candidate_source_id: availableSources?.find(
            (source) =>
              source?.id ===
              (enableApi
                ? contactData?.ats_candidate_source?.id
                : ats_candidate_source?.id)
          )?.id,
        }}
      />
      {errorsActions?.isOpen && (
        <BulkError
          total={candidateId?.length}
          contacts={errorContacts}
          modalActions={errorsActions}
          mainText={`Candidate action${
            errorContacts?.length > 1 ? 's' : ''
          } failed`}
          successMessage="successfully added to job"
          onActionSubmit={(close) => {
            close();
          }}
          mainSubText={
            errorContacts?.length > 1
              ? `These candidates are already present in the specified job, so this action is not completed.`
              : `This candidate is already present in the specified job, so this action is not completed.`
          }
        />
      )}
    </>
  );
};
